import React, { useCallback, useEffect, useRef, useState } from "react";
import { IonSpinner, IonText } from "@ionic/react";

import {
  Recurso,
  guardarProgreso,
  urlEmbed,
} from "src/services/recursos";

/**
 * Reproductor de un recurso de la biblioteca.
 *
 * Criterios de optimización (el video puede durar varios minutos y muchos
 * funcionarios lo miran con datos móviles):
 *
 *  - Streaming adaptativo (HLS) cuando está disponible: el video llega en
 *    segmentos y la calidad se ajusta sola al ancho de banda. Nadie descarga
 *    el archivo completo para ver los primeros 30 segundos.
 *  - `hls.js` se carga con import dinámico, así que sus ~400 KB no entran al
 *    bundle inicial de la app: solo se bajan cuando alguien abre un video HLS.
 *  - `preload="none"`: abrir la lista de recursos no consume datos. La
 *    transferencia arranca recién cuando el usuario toca play.
 *  - El progreso se persiste cada 15 s, no en cada `timeupdate` (que dispara
 *    ~4 veces por segundo).
 *
 * Limitación conocida: en YouTube/Vimeo el contenido corre dentro de un iframe
 * de terceros y no podemos medir el avance sin cargar su SDK. Esos recursos no
 * registran progreso; para capacitaciones con evidencia de visualización hay
 * que subir el video (proveedor CLOUDINARY).
 */

interface Props {
  recurso: Recurso;
  /** Se dispara cuando el backend confirma un avance (para refrescar la lista). */
  onProgreso?: (porcentaje: number, completado: boolean) => void;
}

/** Cada cuántos segundos reproducidos persistimos el avance. */
const INTERVALO_GUARDADO_SEG = 15;

/**
 * Salto máximo entre dos `timeupdate` que contamos como reproducción real.
 * Un delta mayor significa que el usuario arrastró la barra: no queremos que
 * adelantar el video cuente como haberlo visto.
 */
const DELTA_MAXIMO_SEG = 1.5;

/**
 * Fuente progresiva que sirve de plan B si el HLS falla.
 *
 * Excluye explícitamente el caso en que `url_externa` ES el mismo .m3u8 (pasa
 * con el proveedor URL cuando el admin pega un manifiesto directo): reintentar
 * la misma URL que acaba de fallar no aporta nada.
 */
const fuenteProgresivaDe = (recurso: Recurso): string | null => {
  if (recurso.url_mp4) return recurso.url_mp4;
  if (recurso.url_externa && recurso.url_externa !== recurso.url_hls) return recurso.url_externa;
  return null;
};

const ReproductorRecurso: React.FC<Props> = ({ recurso, onProgreso }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cargandoMotor, setCargandoMotor] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Evita un bucle si la fuente progresiva también falla. */
  const fallbackUsadoRef = useRef(false);

  /* Refs en lugar de estado: estos valores cambian muchas veces por segundo y
     no deben provocar re-render ni quedar capturados en closures viejos. */
  const segundosVistosRef = useRef<number>(recurso.progreso?.segundos_vistos ?? 0);
  const ultimoGuardadoRef = useRef<number>(recurso.progreso?.segundos_vistos ?? 0);
  const ultimoTiempoRef = useRef<number>(0);
  const posicionRef = useRef<number>(recurso.progreso?.ultima_posicion_seg ?? 0);

  const embed = urlEmbed(recurso);
  const esIframe = Boolean(embed);

  const persistir = useCallback(async () => {
    if (esIframe) return;
    const segundos = segundosVistosRef.current;
    if (segundos <= ultimoGuardadoRef.current) return;

    ultimoGuardadoRef.current = segundos;
    const resultado = await guardarProgreso(recurso.id, segundos, posicionRef.current);
    if (resultado && onProgreso) {
      onProgreso(resultado.porcentaje, resultado.completado);
    }
  }, [recurso.id, esIframe, onProgreso]);

  /* Monta la fuente en el <video>. Para HLS usa el soporte nativo (Safari/iOS)
     y si no está disponible cae a hls.js sobre Media Source Extensions. */
  useEffect(() => {
    if (esIframe) return;

    const video = videoRef.current;
    if (!video) return;

    let hlsInstancia: any = null;
    let cancelado = false;

    const montar = async () => {
      setError(null);

      const fuenteProgresiva = fuenteProgresivaDe(recurso);

      if (recurso.url_hls) {
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = recurso.url_hls;
          return;
        }

        setCargandoMotor(true);
        try {
          const modulo = await import("hls.js");
          const Hls = modulo.default;
          if (cancelado) return;

          if (Hls.isSupported()) {
            hlsInstancia = new Hls({
              // No adelantar el buffer más de lo necesario: en datos móviles
              // precargar minutos de video que quizá no se vean es plata tirada.
              maxBufferLength: 30,
              maxMaxBufferLength: 60,
              startLevel: -1, // que el ABR elija la calidad inicial
            });

            // Si el manifiesto no existe (el CDN todavía no terminó de generar
            // las variantes, o el plan no incluye streaming adaptativo) caemos
            // a la fuente progresiva en lugar de dejar la pantalla en negro.
            hlsInstancia.on(Hls.Events.ERROR, (_evento: any, datos: any) => {
              if (!datos?.fatal) return;
              if (!fuenteProgresiva || fallbackUsadoRef.current) {
                setError("No se pudo reproducir el video.");
                return;
              }
              fallbackUsadoRef.current = true;
              // El destroy() se difiere: hls.js está ejecutando sus propios
              // handlers en este momento y destruirlo desde adentro puede
              // dejarlo a mitad de camino.
              setTimeout(() => {
                if (cancelado) return;
                hlsInstancia?.destroy();
                hlsInstancia = null;
                video.src = fuenteProgresiva;
                video.load();
              }, 0);
            });

            hlsInstancia.loadSource(recurso.url_hls);
            hlsInstancia.attachMedia(video);
            return;
          }
        } catch {
          /* Sin hls.js disponible seguimos al fallback progresivo. */
        } finally {
          if (!cancelado) setCargandoMotor(false);
        }
      }

      if (fuenteProgresiva) {
        video.src = fuenteProgresiva;
        return;
      }

      setError("Este recurso no tiene un video reproducible.");
    };

    montar();

    return () => {
      cancelado = true;
      if (hlsInstancia) hlsInstancia.destroy();
    };
  }, [recurso.id, recurso.url_hls, recurso.url_mp4, recurso.url_externa, esIframe]);

  /* Al desmontar guardamos lo que quedó pendiente.
     Va por ref y con dependencias vacías a propósito: si el efecto dependiera
     de `persistir`, cada re-render del padre cambiaría su identidad, React
     ejecutaría la limpieza anterior y dispararíamos un POST por render. */
  const persistirRef = useRef(persistir);
  persistirRef.current = persistir;
  useEffect(
    () => () => {
      void persistirRef.current();
    },
    []
  );

  const alCargarMetadatos = () => {
    const video = videoRef.current;
    if (!video) return;
    ultimoTiempoRef.current = video.currentTime;

    // Reanudar donde quedó, salvo que esté prácticamente al final.
    const posicion = recurso.progreso?.ultima_posicion_seg ?? 0;
    if (posicion > 5 && video.duration && posicion < video.duration - 10) {
      video.currentTime = posicion;
      ultimoTiempoRef.current = posicion;
    }
  };

  /* Cubre el fallo del HLS nativo (Safari/iOS) y el de la propia fuente mp4. */
  const alFallarVideo = () => {
    const video = videoRef.current;
    const fuenteProgresiva = fuenteProgresivaDe(recurso);

    if (video && fuenteProgresiva && !fallbackUsadoRef.current && video.src !== fuenteProgresiva) {
      fallbackUsadoRef.current = true;
      video.src = fuenteProgresiva;
      video.load();
      return;
    }
    setError("No se pudo reproducir el video.");
  };

  const alActualizarTiempo = () => {
    const video = videoRef.current;
    if (!video) return;

    const actual = video.currentTime;
    const delta = actual - ultimoTiempoRef.current;
    ultimoTiempoRef.current = actual;
    posicionRef.current = actual;

    // Solo acumulamos avance continuo hacia adelante.
    if (delta > 0 && delta < DELTA_MAXIMO_SEG) {
      segundosVistosRef.current += delta;
    }

    if (segundosVistosRef.current - ultimoGuardadoRef.current >= INTERVALO_GUARDADO_SEG) {
      void persistir();
    }
  };

  if (esIframe) {
    return (
      <div className="recurso-video-marco">
        <iframe
          src={embed as string}
          title={recurso.titulo}
          frameBorder={0}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <>
      <div className="recurso-video-marco">
        <video
          ref={videoRef}
          poster={recurso.url_poster || undefined}
          controls
          playsInline
          preload="none"
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          onLoadedMetadata={alCargarMetadatos}
          onTimeUpdate={alActualizarTiempo}
          onPause={() => void persistir()}
          onEnded={() => void persistir()}
          onError={alFallarVideo}
        />
        {cargandoMotor && (
          <div className="recurso-video-overlay">
            <IonSpinner name="crescent" />
          </div>
        )}
      </div>

      {error && (
        <IonText color="danger">
          <p className="ion-padding-start ion-padding-end">{error}</p>
        </IonText>
      )}
    </>
  );
};

export default ReproductorRecurso;
